import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  Tab,
  Tabs,
  useTheme,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Card,
  CardContent,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  TrendingUp,
  TrendingDown,
  MoreVert,
  Download,
  Share,
  CalendarToday,
  AccountBalance,
  Description,
  Assessment,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Données de démonstration
const monthlyData = [
  { month: 'Jan', documents: 65, revenue: 4800, clients: 12 },
  { month: 'Fév', documents: 78, revenue: 5200, clients: 15 },
  { month: 'Mar', documents: 82, revenue: 5800, clients: 18 },
  { month: 'Avr', documents: 95, revenue: 6400, clients: 22 },
  { month: 'Mai', documents: 88, revenue: 6100, clients: 20 },
  { month: 'Jun', documents: 102, revenue: 7200, clients: 25 },
];

const documentTypes = [
  { name: 'Contrats', value: 35 },
  { name: 'Factures', value: 25 },
  { name: 'Statuts', value: 20 },
  { name: 'Attestations', value: 15 },
  { name: 'Autres', value: 5 },
];

const kpiData = [
  {
    title: 'Documents Traités',
    value: '510',
    change: '+12%',
    trend: 'up',
    color: '#2196f3',
    icon: Description,
  },
  {
    title: 'Chiffre d\'Affaires',
    value: '35.5k€',
    change: '+8%',
    trend: 'up',
    color: '#4caf50',
    icon: AccountBalance,
  },
  {
    title: 'Temps Moyen',
    value: '2.3j',
    change: '-15%',
    trend: 'down',
    color: '#ff9800',
    icon: CalendarToday,
  },
  {
    title: 'Score Global',
    value: '92%',
    change: '+5%',
    trend: 'up',
    color: '#f44336',
    icon: Assessment,
  },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const Analytics = () => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState('month');
  const [currentTab, setCurrentTab] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const StatCard = ({ data }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Paper
        sx={{
          p: 3,
          height: '100%',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-4px)',
            transition: 'all 0.3s ease',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: `${data.color}15`,
              color: data.color,
              mr: 2,
            }}
          >
            <data.icon />
          </Box>
          <Typography variant="subtitle2" color="text.secondary">
            {data.title}
          </Typography>
        </Box>
        
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
          {data.value}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {data.trend === 'up' ? (
            <TrendingUp sx={{ color: theme.palette.success.main, mr: 1 }} />
          ) : (
            <TrendingDown sx={{ color: theme.palette.error.main, mr: 1 }} />
          )}
          <Typography
            variant="body2"
            color={data.trend === 'up' ? 'success.main' : 'error.main'}
          >
            {data.change}
          </Typography>
        </Box>
      </Paper>
    </motion.div>
  );

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Analyses & Statistiques
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <ButtonGroup size="small" aria-label="time range selection">
              <Button
                variant={timeRange === 'week' ? 'contained' : 'outlined'}
                onClick={() => handleTimeRangeChange('week')}
              >
                Semaine
              </Button>
              <Button
                variant={timeRange === 'month' ? 'contained' : 'outlined'}
                onClick={() => handleTimeRangeChange('month')}
              >
                Mois
              </Button>
              <Button
                variant={timeRange === 'year' ? 'contained' : 'outlined'}
                onClick={() => handleTimeRangeChange('year')}
              >
                Année
              </Button>
            </ButtonGroup>

            <IconButton onClick={handleMenuClick}>
              <MoreVert />
            </IconButton>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {kpiData.map((kpi, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <StatCard data={kpi} />
            </Grid>
          ))}

          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={currentTab} onChange={handleTabChange}>
                  <Tab label="Vue Générale" />
                  <Tab label="Documents" />
                  <Tab label="Performance" />
                </Tabs>
              </Box>

              {currentTab === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Évolution Mensuelle
                  </Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <AreaChart data={monthlyData}>
                      <defs>
                        <linearGradient id="colorDocuments" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2196f3" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#2196f3" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#4caf50" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="documents"
                        stroke="#2196f3"
                        fillOpacity={1}
                        fill="url(#colorDocuments)"
                      />
                      <Area
                        yAxisId="right"
                        type="monotone"
                        dataKey="revenue"
                        stroke="#4caf50"
                        fillOpacity={1}
                        fill="url(#colorRevenue)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              )}

              {currentTab === 1 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Typography variant="h6" gutterBottom>
                      Distribution des Documents
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="documents" fill="#2196f3" />
                        <Bar dataKey="clients" fill="#4caf50" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" gutterBottom>
                      Types de Documents
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          data={documentTypes}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={120}
                          label
                        >
                          {documentTypes.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Grid>
                </Grid>
              )}

              {currentTab === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Indicateurs de Performance
                  </Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="documents"
                        stroke="#2196f3"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="clients"
                        stroke="#4caf50"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleMenuClose}>
            <Download sx={{ mr: 1 }} /> Exporter les données
          </MenuItem>
          <MenuItem onClick={handleMenuClose}>
            <Share sx={{ mr: 1 }} /> Partager le rapport
          </MenuItem>
        </Menu>
      </motion.div>
    </Box>
  );
};

export default Analytics; 